<template>
    <div class="login-page">
        <!-- Back to Home -->
        <router-link to="/" class="back-home">
            <i class="fas fa-arrow-left"></i>
            <span>返回首页</span>
        </router-link>

        <!-- Left promotional area -->
        <div class="promo-section">
            <div class="promo-content">
                <div class="brand">
                    <div class="brand-icon">
                        <i class="fas fa-calculator"></i>
                    </div>
                    <h1>高中数学组卷系统</h1>
                </div>

                <p class="tagline">智能组卷，高效教学</p>

                <div class="features">
                    <div class="feature-item">
                        <div class="feature-icon">
                            <i class="fas fa-database"></i>
                        </div>
                        <div class="feature-text">
                            <h3>海量题库</h3>
                            <p>涵盖高中数学全部知识点，精选历年真题</p>
                        </div>
                    </div>

                    <div class="feature-item">
                        <div class="feature-icon">
                            <i class="fas fa-magic"></i>
                        </div>
                        <div class="feature-text">
                            <h3>智能筛选</h3>
                            <p>按题型、难度、年份、地区精准筛选</p>
                        </div>
                    </div>

                    <div class="feature-item">
                        <div class="feature-icon">
                            <i class="fas fa-file-alt"></i>
                        </div>
                        <div class="feature-text">
                            <h3>一键组卷</h3>
                            <p>快速生成 Word 试卷，支持自定义排版</p>
                        </div>
                    </div>

                    <div class="feature-item">
                        <div class="feature-icon">
                            <i class="fas fa-users"></i>
                        </div>
                        <div class="feature-text">
                            <h3>师生共用</h3>
                            <p>教师组卷、学生刷题，一站式服务</p>
                        </div>
                    </div>
                </div>

                <div class="stats">
                    <div class="stat-item">
                        <span class="stat-num">10000+</span>
                        <span class="stat-label">精选试题</span>
                    </div>
                    <div class="stat-item">
                        <span class="stat-num">500+</span>
                        <span class="stat-label">知识点</span>
                    </div>
                    <div class="stat-item">
                        <span class="stat-num">100+</span>
                        <span class="stat-label">学校使用</span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Right login form area -->
        <div class="form-section">
            <div class="form-container">
                <div class="form-header">
                    <h2>欢迎回来</h2>
                    <p>登录您的账号，开始智能组卷</p>
                </div>

                <Form :model="form" :rules="rules" ref="loginForm" class="login-form">
                    <FormItem prop="username">
                        <div class="input-group">
                            <i class="fas fa-user"></i>
                            <Input v-model="form.username" placeholder="请输入用户名" size="large"
                                @keyup.enter="handleLogin" />
                        </div>
                    </FormItem>
                    <FormItem prop="password">
                        <div class="input-group">
                            <i class="fas fa-lock"></i>
                            <Input type="password" v-model="form.password" placeholder="请输入密码" size="large"
                                @keyup.enter="handleLogin" />
                        </div>
                    </FormItem>
                    <div class="form-options">
                        <Checkbox v-model="rememberMe">记住我</Checkbox>
                        <a class="forgot-pwd">忘记密码？</a>
                    </div>
                    <FormItem>
                        <Button type="primary" @click="handleLogin" long size="large" :loading="loading"
                            class="login-btn">
                            <i class="fas fa-sign-in-alt" v-if="!loading"></i>
                            登录
                        </Button>
                    </FormItem>
                </Form>

                <div class="form-footer">
                    <p>还没有账号？<a @click="goToRegister">立即注册</a></p>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import api, { setAuthToken, setUser } from '@/utils/api';

export default {
    name: "Login",
    data() {
        return {
            form: {
                username: "",
                password: "",
            },
            rememberMe: true,
            rules: {
                username: [
                    { required: true, message: "请输入用户名", trigger: "blur" },
                ],
                password: [
                    { required: true, message: "请输入密码", trigger: "blur" },
                ],
            },
            loading: false,
        };
    },
    mounted() {
        const username = localStorage.getItem('remember_username');
        const password = localStorage.getItem('remember_password');
        if (username && password) {
            this.form.username = username;
            this.form.password = atob(password);
            this.rememberMe = true;
        }
    },
    methods: {
        async handleLogin() {
            this.$refs.loginForm.validate(async (valid) => {
                if (!valid) {
                    return;
                }

                this.loading = true;
                try {
                    const response = await api.post("/api/login", {
                        username: this.form.username,
                        password: this.form.password,
                    });

                    const data = response.data;

                    // Store token and user info
                    setAuthToken(data.token);
                    setUser(data.user);

                    // Handle Remember Me
                    if (this.rememberMe) {
                        localStorage.setItem('remember_username', this.form.username);
                        localStorage.setItem('remember_password', btoa(this.form.password));
                    } else {
                        localStorage.removeItem('remember_username');
                        localStorage.removeItem('remember_password');
                    }

                    this.$Message.success("登录成功！");

                    // Redirect to original page or home
                    const redirect = this.$route.query.redirect || '/';
                    this.$router.push(redirect);
                } catch (error) {
                    console.error("Login error:", error);
                    if (error.response && error.response.data) {
                        this.$Message.error(error.response.data.error || "登录失败");
                    } else {
                        this.$Message.error("登录出错");
                    }
                } finally {
                    this.loading = false;
                }
            });
        },
        goToRegister() {
            this.$router.push({ name: "Register" });
        },
    },
};
</script>

<style scoped>
.login-page {
    min-height: 100vh;
    display: flex;
    background: #fff;
    position: relative;
}

.back-home {
    position: absolute;
    top: 30px;
    right: 40px;
    z-index: 10;
    display: flex;
    align-items: center;
    gap: 8px;
    color: #64748b;
    font-weight: 500;
    transition: all 0.3s;
    text-decoration: none;
    padding: 8px 16px;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.8);
    backdrop-filter: blur(4px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.back-home:hover {
    color: #667eea;
    background: #fff;
    transform: translateX(-4px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

/* Left promotional section */
.promo-section {
    flex: 1.2;
    background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 60px;
    color: white;
    position: relative;
    overflow: hidden;
}

.promo-section::before {
    content: '';
    position: absolute;
    top: -10%;
    left: -10%;
    width: 120%;
    height: 120%;
    background: radial-gradient(circle at center, rgba(102, 126, 234, 0.15) 0%, transparent 70%);
    animation: pulse 20s ease-in-out infinite;
}

@keyframes pulse {

    0%,
    100% {
        transform: scale(1) translate(0, 0);
        opacity: 0.5;
    }

    50% {
        transform: scale(1.1) translate(2%, 2%);
        opacity: 0.8;
    }
}

.promo-content {
    position: relative;
    z-index: 1;
    max-width: 540px;
    animation: fadeIn 0.8s ease-out;
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(20px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.brand {
    display: flex;
    align-items: center;
    gap: 20px;
    margin-bottom: 24px;
}

.brand-icon {
    width: 64px;
    height: 64px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 30px;
    box-shadow: 0 8px 16px rgba(102, 126, 234, 0.3);
}

.brand h1 {
    font-size: 38px;
    font-weight: 800;
    margin: 0;
    letter-spacing: -0.5px;
    background: linear-gradient(90deg, #fff 0%, #cbd5e1 100%);
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
}

.tagline {
    font-size: 20px;
    color: rgba(255, 255, 255, 0.8);
    margin-bottom: 56px;
    font-weight: 500;
}

.features {
    display: flex;
    flex-direction: column;
    gap: 32px;
    margin-bottom: 56px;
}

.feature-item {
    display: flex;
    gap: 20px;
    align-items: flex-start;
    transition: transform 0.3s;
}

.feature-item:hover {
    transform: translateX(10px);
}

.feature-icon {
    width: 48px;
    height: 48px;
    background: rgba(255, 255, 255, 0.08);
    border-radius: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #a5b4fc;
    font-size: 20px;
    flex-shrink: 0;
    backdrop-filter: blur(8px);
    border: 1px solid rgba(255, 255, 255, 0.1);
}

.feature-text h3 {
    margin: 0 0 6px 0;
    font-size: 18px;
    font-weight: 600;
    color: #f8fafc;
}

.feature-text p {
    margin: 0;
    font-size: 15px;
    color: rgba(255, 255, 255, 0.6);
    line-height: 1.6;
}

.stats {
    display: flex;
    gap: 48px;
    padding-top: 32px;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.stat-item {
    display: flex;
    flex-direction: column;
}

.stat-num {
    font-size: 32px;
    font-weight: 800;
    color: #a5b4fc;
    margin-bottom: 4px;
}

.stat-label {
    font-size: 14px;
    color: rgba(255, 255, 255, 0.5);
    font-weight: 500;
}

/* Right form section */
.form-section {
    flex: 1;
    background: #f8fafc;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 60px;
}

.form-container {
    width: 100%;
    max-width: 400px;
    background: #fff;
    padding: 48px;
    border-radius: 24px;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.03);
    animation: slideIn 0.8s cubic-bezier(0.16, 1, 0.3, 1);
}

@keyframes slideIn {
    from {
        opacity: 0;
        transform: translateX(30px);
    }

    to {
        opacity: 1;
        transform: translateX(0);
    }
}

.form-header {
    margin-bottom: 40px;
    text-align: center;
}

.form-header h2 {
    font-size: 32px;
    font-weight: 800;
    color: #1e293b;
    margin: 0 0 12px 0;
    letter-spacing: -1px;
}

.form-header p {
    font-size: 16px;
    color: #64748b;
    margin: 0;
}

.login-form {
    margin-bottom: 24px;
}

.input-group {
    position: relative;
}

.input-group i {
    position: absolute;
    left: 16px;
    top: 50%;
    transform: translateY(-50%);
    color: #94a3b8;
    z-index: 1;
    font-size: 18px;
}

.input-group :deep(.ivu-input) {
    padding-left: 48px;
    height: 54px;
    border-radius: 12px;
    border: 2px solid #f1f5f9;
    background: #f8fafc;
    font-size: 16px;
    transition: all 0.3s;
}

.input-group :deep(.ivu-input:focus) {
    border-color: #667eea;
    background: #fff;
    box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.1);
}

.form-options {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
    padding: 0 4px;
}

.forgot-pwd {
    color: #64748b;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
}

.forgot-pwd:hover {
    color: #667eea;
}

.login-btn {
    height: 54px;
    border-radius: 12px;
    font-size: 18px;
    font-weight: 700;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    box-shadow: 0 8px 20px rgba(102, 126, 234, 0.3);
    transition: all 0.3s;
}

.login-btn:hover {
    background: linear-gradient(135deg, #5a6fd6 0%, #6a4190 100%);
    transform: translateY(-2px);
    box-shadow: 0 12px 24px rgba(102, 126, 234, 0.4);
}

.form-footer {
    text-align: center;
}

.form-footer p {
    color: #64748b;
    font-size: 15px;
}

.form-footer a {
    color: #667eea;
    cursor: pointer;
    font-weight: 700;
    margin-left: 4px;
}

.form-footer a:hover {
    color: #764ba2;
    text-decoration: underline;
}

/* Responsive */
@media (max-width: 1200px) {
    .promo-section {
        padding: 40px;
    }

    .form-section {
        padding: 40px;
    }
}

@media (max-width: 1024px) {
    .login-page {
        flex-direction: column;
    }

    .back-home {
        top: 20px;
        right: 20px;
    }

    .promo-section {
        flex: none;
        min-height: 480px;
        padding: 60px 40px;
    }

    .form-section {
        padding: 60px 20px;
    }

    .form-container {
        padding: 40px 30px;
    }
}

@media (max-width: 640px) {
    .promo-section {
        padding: 40px 24px;
    }

    .brand h1 {
        font-size: 30px;
    }

    .tagline {
        margin-bottom: 40px;
    }

    .features {
        display: none;
    }

    .stats {
        justify-content: space-between;
        gap: 20px;
    }

    .stat-num {
        font-size: 24px;
    }
}
</style>
